import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import lessonLearnedService from '../../services/lessonLearnedService';
import projectService from '../../services/projectService';

const LessonLearnedEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    title: '',
    category: 'Other',
    type: 'Best Practice',
    phase: '',
    situation: '',
    action_taken: '',
    result: '',
    lesson: '',
    recommendation: '',
    impact: 'Medium',
    applicability: 'Project-Specific',
    date_identified: '',
    status: 'Draft',
    is_shared: false
  });

  const categories = [
    'Technical', 'Process', 'Communication', 'Resource Management',
    'Risk Management', 'Quality', 'Stakeholder Management', 'Budget',
    'Schedule', 'Team Dynamics', 'Tools & Technology', 'Other'
  ];

  const types = ['Success', 'Challenge', 'Best Practice', 'Mistake', 'Improvement'];
  const phases = ['Initiation', 'Planning', 'Execution', 'Monitoring & Control', 'Closure', 'Post-Project'];
  const impacts = ['High', 'Medium', 'Low'];
  const applicabilities = ['Project-Specific', 'Department-Wide', 'Organization-Wide', 'Industry-Wide'];
  const statuses = ['Draft', 'Under Review', 'Approved', 'Archived'];

  useEffect(() => {
    fetchLessonLearned();
    fetchProjects();
  }, [id]);

  const fetchLessonLearned = async () => {
    try {
      setLoading(true);
      const data = await lessonLearnedService.getLessonLearned(id);
      setFormData({
        project_id: data.project_id || '',
        title: data.title || '',
        category: data.category || 'Other',
        type: data.type || 'Best Practice',
        phase: data.phase || '',
        situation: data.situation || '',
        action_taken: data.action_taken || '',
        result: data.result || '',
        lesson: data.lesson || '',
        recommendation: data.recommendation || '',
        impact: data.impact || 'Medium',
        applicability: data.applicability || 'Project-Specific',
        date_identified: data.date_identified || '',
        status: data.status || 'Draft',
        is_shared: data.is_shared || false
      });
    } catch (error) {
      toast.error('Failed to fetch lesson learned');
      navigate('/app/lessons-learned');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await projectService.getAllProjects({ limit: 1000 });
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      await lessonLearnedService.updateLessonLearned(id, formData);
      toast.success('Lesson learned updated successfully');
      navigate('/app/lessons-learned');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update lesson learned');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Edit Lesson Learned</h1>
        <p className="text-gray-600 mt-1">Update lesson learned information</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">

        {/* Basic Information */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project <span className="text-red-500">*</span>
              </label>
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief title for the lesson"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {types.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phase</label>
              <select
                name="phase"
                value={formData.phase}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select phase</option>
                {phases.map(phase => (
                  <option key={phase} value={phase}>{phase}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Impact <span className="text-red-500">*</span>
              </label>
              <select
                name="impact"
                value={formData.impact}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {impacts.map(impact => (
                  <option key={impact} value={impact}>{impact}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicability <span className="text-red-500">*</span>
              </label>
              <select
                name="applicability"
                value={formData.applicability}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {applicabilities.map(app => (
                  <option key={app} value={app}>{app}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Identified <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="date_identified"
                value={formData.date_identified}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Lesson Details */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Lesson Details (STAR Format)</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Situation <span className="text-red-500">*</span>
              </label>
              <textarea
                name="situation"
                value={formData.situation}
                onChange={handleChange}
                required
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the context and situation..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Taken</label>
              <textarea
                name="action_taken"
                value={formData.action_taken}
                onChange={handleChange}
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="What actions were taken..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Result</label>
              <textarea
                name="result"
                value={formData.result}
                onChange={handleChange}
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="What was the outcome..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lesson Learned <span className="text-red-500">*</span>
              </label>
              <textarea
                name="lesson"
                value={formData.lesson}
                onChange={handleChange}
                required
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="The key lesson learned..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recommendation</label>
              <textarea
                name="recommendation"
                value={formData.recommendation}
                onChange={handleChange}
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Recommendations for future projects..."
              />
            </div>
          </div>
        </div>

        {/* Additional Settings */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Settings</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_shared"
                checked={formData.is_shared}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Share with organization
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/app/lessons-learned')}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <FiX /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LessonLearnedEdit;

